import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button, Input, Alert } from "../components/UI";
import { Mail, Lock, User, Phone, MapPin, Leaf } from "lucide-react";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "", phone: "", location: "" });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!form.email) e.email = "Please enter your email";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Invalid email address";
    if (!form.password || form.password.length < 6) e.password = "Password must be at least 6 characters";
    if (form.password !== form.confirm) e.confirm = "Passwords do not match";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }
    setErrors({});
    setApiError("");
    setLoading(true);
    try {
      await register({ name: form.name, email: form.email, password: form.password, phone: form.phone, location: form.location });
      navigate("/dashboard");
    } catch (err) {
      setApiError(err?.response?.data?.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-white to-earth-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-slide-up">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-600 text-white rounded-2xl shadow-lg mb-4">
            <Leaf size={32} />
          </div>
          <h1 className="text-3xl font-900 text-brand-800">Join AgriHealth!</h1>
          <p className="text-brand-500 font-600 mt-1">Free account — no credit card needed</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-brand-100 p-6 lg:p-8">
          {apiError && <div className="mb-4"><Alert type="error" onDismiss={() => setApiError("")}>{apiError}</Alert></div>}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <Input label="👤 Full Name" placeholder="e.g. John Kamau" value={form.name} onChange={set("name")} error={errors.name} icon={User} />
            <Input label="📧 Email" type="email" placeholder="yourname@email.com" value={form.email} onChange={set("email")} error={errors.email} icon={Mail} />
            <Input label="📱 Phone Number (optional)" type="tel" placeholder="+254 700 000000" value={form.phone} onChange={set("phone")} icon={Phone} />
            <Input label="📍 Location (optional)" placeholder="e.g. Nakuru, Kenya" value={form.location} onChange={set("location")} icon={MapPin} />
            <Input label="🔒 Password" type="password" placeholder="At least 6 characters" value={form.password} onChange={set("password")} error={errors.password} icon={Lock} />
            <Input label="🔒 Confirm Password" type="password" placeholder="Type password again" value={form.confirm} onChange={set("confirm")} error={errors.confirm} icon={Lock} />

            <Button type="submit" size="lg" loading={loading} className="w-full mt-2">
              🌱 Create My Free Account
            </Button>
          </form>
        </div>

        <p className="text-center mt-6 text-brand-600 font-700">
          Already have an account?{" "}
          <Link to="/login" className="text-brand-700 underline font-800 hover:text-brand-900">Sign In</Link>
        </p>

        <div className="flex justify-center gap-6 mt-8 text-xs text-brand-400 font-600">
          <span>✅ 100% Free</span>
          <span>🔒 Your data is safe</span>
          <span>📵 No spam</span>
        </div>
      </div>
    </div>
  );
}
